import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { AiOutlineLoading } from "react-icons/ai";
import { HiCheckCircle, HiXCircle, HiShieldCheck, HiGlobeAlt, HiUserGroup } from "react-icons/hi";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import TextField from "./components/TextField";
import PasswordField from "./components/PasswordField";
import MessageBox from "./components/MessageBox";
import dosee_logo from "../../../assets/images/dosee.png";
import { useAdminContext } from "../../context/admin/context/AdminContext";
import { defaultImage } from "../../middlewares/libraries/imageKit/ImageKit";
import { allowedCountries, countriesList } from "../home/component/data";

const strengthSegments = 5;

const getPasswordHints = (pwd = "") => {
  const hasLen6 = pwd.length >= 6;
  const hasLen10 = pwd.length >= 10;
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasNumber = /\d/.test(pwd);
  const hasSymbol = /[^A-Za-z0-9]/.test(pwd);

  let score = 0;
  if (hasLen6) score += 1;
  if (hasLen10) score += 1;
  if (hasLower) score += 1;
  if (hasUpper) score += 1;
  if (hasNumber) score += 1;
  if (hasSymbol) score += 1;
  const clamped = Math.min(strengthSegments, score);

  let label = "Too weak";
  if (clamped >= 4) label = "Strong";
  else if (clamped === 3) label = "Good";
  else if (clamped === 2) label = "Fair";

  return {
    label,
    score: clamped,
    checks: { hasLen6, hasLen10, hasLower, hasUpper, hasNumber, hasSymbol },
  };
};

const HintItem = ({ ok, children }) => (
  <li className="flex items-center gap-1.5 text-[11px]">
    {ok ? (
      <HiCheckCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
    ) : (
      <HiXCircle className="h-3.5 w-3.5 text-gray-300 shrink-0" />
    )}
    <span className={ok ? "text-gray-700" : "text-gray-500"}>{children}</span>
  </li>
);

const ValueProp = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-900/50 text-emerald-400">
      <Icon size={24} />
    </div>
    <div>
      <h3 className="font-semibold text-white">{title}</h3>
      <p className="mt-1 text-sm text-zinc-400">{description}</p>
    </div>
  </div>
);

const SignUp = () => {
  const { adminSignUp, sysAdmin } = useAdminContext();

  // Form State
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phonenumber, setPhonenumber] = useState("");
  const [country, setCountry] = useState("Uganda");
  const [countryCode, setCountryCode] = useState("ug");
  const [password, setPassword] = useState("");
  const [cPassword, setCPassword] = useState("");

  // UI State
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleResponse = (isError, msg) => {
    setError(isError);
    setSuccess(!isError);
    setLoading(false);
    setErrorMessage(msg);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setSuccess(false);

    // Streamlined Validation
    if (!name.trim()) return handleResponse(true, "Company name is required");
    if (!firstName.trim()) return handleResponse(true, "First name is required");
    if (!lastName.trim()) return handleResponse(true, "Last name is required");
    if (!email.trim()) return handleResponse(true, "Email is required");
    if (!phonenumber) return handleResponse(true, "Phone number is required");
    if (!country || country === "Select Country") return handleResponse(true, "Country is required");
    if (!password) return handleResponse(true, "Password is required");
    if (password.length < 6) return handleResponse(true, "Minimum password size is 6 characters");
    if (password !== cPassword) return handleResponse(true, "Passwords do not match");

    const { message: apiMsg, status } = await adminSignUp({
      name: name.trim(),
      thumbnail: defaultImage,
      description: name.trim(),
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      password,
      phonenumber,
      country,
    });

    if (status !== 200 && status !== 201) {
      handleResponse(true, apiMsg);
    } else {
      handleResponse(false, "Account created successfully! Check your email.");
    }
  };

  const pwdHints = useMemo(() => getPasswordHints(password), [password]);
  const passwordsMatch = password && cPassword && password === cPassword;

  if (sysAdmin !== null) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className="flex min-h-screen w-full bg-white font-sans text-gray-900">

      {/* Left Column: Brand & Value Props (Desktop Only) */}
      <aside className="relative hidden w-[45%] flex-col justify-between overflow-hidden bg-zinc-950 p-12 lg:flex">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-emerald-900/20 via-zinc-950 to-zinc-950" />
        <div className="absolute -left-24 top-24 h-96 w-96 rounded-full bg-emerald-900/10 blur-3xl" />

        <div className="relative z-10">
          <img src={dosee_logo} alt="Dósee Africa" className="h-10 w-auto brightness-0 invert" />
        </div>

        <div className="relative z-10 my-auto max-w-md py-12">
          <h2 className="text-4xl font-bold tracking-tight text-white">
            Simplify Tourism Across Africa.
          </h2>
          <p className="mt-4 text-lg font-light leading-relaxed text-zinc-400">
            Join the continent's fastest-growing platform. Register your company to publish authentic experiences and connect with global travelers.
          </p>

          <div className="mt-12 space-y-8">
            <ValueProp
              icon={HiGlobeAlt}
              title="Global Reach"
              description="Put your experiences in front of an international audience actively seeking authentic African travel."
            />
            <ValueProp
              icon={HiShieldCheck}
              title="Verified & Secure"
              description="Industry-standard encryption and built-in compliance checks keep your business and payments safe."
            />
            <ValueProp
              icon={HiUserGroup}
              title="Community Impact"
              description="Partner with a platform that actively champions eco-friendly practices and local community development."
            />
          </div>
        </div>

        <div className="relative z-10 text-xs text-zinc-500">
          © {new Date().getFullYear()} Dósee Africa. All rights reserved.
        </div>
      </aside>

      {/* Right Column: Registration Form */}
      <main className="flex w-full items-center justify-center p-6 sm:p-8 lg:w-[55%] lg:p-12 xl:p-16">
        <div className="w-full max-w-lg">

          {/* Mobile Logo */}
          <div className="mb-8 flex justify-center lg:hidden">
            <img src={dosee_logo} alt="Dósee Africa" className="h-12 w-auto" />
          </div>

          <header className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Create Account</h1>
            <p className="mt-2 text-sm text-gray-600">
              Register your company to start managing experiences.
            </p>
          </header>

          <form onSubmit={handleSignUp} className="space-y-5">

            {/* Company Info */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Company Name</label>
              <TextField
                type="text"
                value={name}
                placeholder="e.g. Serengeti Safaris Ltd."
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Personal Info Grid */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">First Name</label>
                <TextField
                  type="text"
                  value={firstName}
                  placeholder="Jane"
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Last Name</label>
                <TextField
                  type="text"
                  value={lastName}
                  placeholder="Doe"
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Email Address</label>
              <TextField
                value={email}
                type="email"
                placeholder="jane@company.com"
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Region & Phone Grid */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Country</label>
                <div className="relative">
                  <select
                    value={country}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val !== "Select Country") {
                        const pickedCountry = countriesList?.find((_c) => _c.name === val);
                        setCountry(pickedCountry.name);
                        setCountryCode(pickedCountry.code);
                      } else {
                        setCountry("Uganda");
                        setCountryCode("ug");
                      }
                    }}
                    className="block w-full appearance-none rounded-lg border border-gray-300 bg-white px-4 py-3.5 text-sm text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    <option value="Select Country">Select Country</option>
                    {countriesList?.map((ctry, index) => (
                      <option key={index} value={ctry.name}>{ctry.name}</option>
                    ))}
                  </select>
                  <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▾</span>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Phone Number</label>
                <PhoneInput
                  country={countryCode}
                  preferredCountries={["ug", "cd", "ke"]}
                  onlyCountries={allowedCountries}
                  enableSearch={true}
                  autoFormat={true}
                  disableDropdown={false}
                  value={phonenumber}
                  onChange={(phone) => setPhonenumber(phone)}
                  inputStyle={{
                    width: "100%",
                    height: "48px",
                    borderColor: "#D1D5DB",
                    borderRadius: "0.5rem",
                    fontSize: "0.875rem",
                    color: "#111827",
                  }}
                  buttonStyle={{
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                    borderTopLeftRadius: "0.5rem",
                    borderBottomLeftRadius: "0.5rem",
                  }}
                />
              </div>
            </div>

            {/* Security */}
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
              <div className="mb-4">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  {password && (
                    <span className="text-xs text-gray-500">
                      Strength: <span className="font-semibold text-gray-900">{pwdHints.label}</span>
                    </span>
                  )}
                </div>
                <PasswordField
                  password={password}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  placeholder="Create a strong password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(false);
                  }}
                />

                {/* Visual Strength Bar */}
                {password && (
                  <div className="mt-2.5 flex items-center gap-1.5" aria-hidden="true">
                    {Array.from({ length: strengthSegments }).map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 w-full rounded-full transition-colors ${i < pwdHints.score ? "bg-emerald-500" : "bg-gray-200"
                          }`}
                      />
                    ))}
                  </div>
                )}

                {/* Compact Hints */}
                <ul className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1.5">
                  <HintItem ok={pwdHints.checks.hasLen6}>Min. 6 characters</HintItem>
                  <HintItem ok={pwdHints.checks.hasLower}>Lowercase letter</HintItem>
                  <HintItem ok={pwdHints.checks.hasUpper}>Uppercase letter</HintItem>
                  <HintItem ok={pwdHints.checks.hasNumber}>Number</HintItem>
                  <HintItem ok={pwdHints.checks.hasSymbol}>Symbol</HintItem>
                </ul>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700">Confirm Password</label>
                <PasswordField
                  password={cPassword}
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                  placeholder="Repeat your password"
                  onChange={(e) => {
                    setCPassword(e.target.value);
                    setError(false);
                  }}
                />
                {cPassword && (
                  <div className="mt-2 text-xs">
                    {passwordsMatch ? (
                      <span className="text-emerald-600 font-medium">Passwords match</span>
                    ) : (
                      <span className="text-red-500">Passwords do not match</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <MessageBox
              error={error}
              success={success}
              message={message}
              onClick={() => {
                setError(false);
                setSuccess(false);
              }}
            />

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex w-full items-center justify-center rounded-lg bg-emerald-700 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? <AiOutlineLoading className="animate-spin text-xl" /> : "Create Account"}
            </button>
          </form>

          <div className="mt-8 flex flex-col items-center gap-4 border-t border-gray-100 pt-8 text-sm">
            <p className="text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-emerald-700 hover:text-emerald-800">
                Sign in here
              </Link>
            </p>
            <p className="text-xs text-gray-500">
              By registering, you agree to our <Link to="/terms" className="underline hover:text-gray-900">Terms</Link> & <Link to="/privacy" className="underline hover:text-gray-900">Privacy Policy</Link>.
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SignUp;